import { KLAX_ACCESS_HEADER, klaxAccessHeader } from 'config/buddy/access-header';
import { BUDDY_CONFIG } from 'config/buddy/config';
import {
  conversationIdForCompletion,
  rememberConversationId,
  runDuringBuddyAsk,
} from 'config/buddy/conversations';
import { clients } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';

function collectConversationIds(parsed: unknown, ids: string[]) {
  if (!parsed || typeof parsed !== 'object') return;
  const record = parsed as Record<string, unknown>;
  if (typeof record.conversationId === 'string' && record.conversationId) {
    ids.push(record.conversationId);
  }
  collectConversationIds(record.response, ids);
  collectConversationIds(record.streamingResponse, ids);
}

export function readCompletionChunks(buffer: string): {
  pieces: string[];
  remaining: string;
  conversationIds: string[];
} {
  const separator = buffer.includes('\r\n\r\n') ? '\r\n\r\n' : '\n\n';
  const parts = buffer.split(separator);
  const remaining = parts.pop() ?? '';
  const pieces: string[] = [];
  const conversationIds: string[] = [];
  parts.forEach((part) => {
    const lines = part.split(/\r?\n/);
    const eventLine = lines.find((line) => line.startsWith('event:'));
    const eventType = eventLine ? eventLine.slice(6).trim() : '';
    lines
      .filter((line) => line.startsWith('data:'))
      .forEach((line) => {
        const raw = line.slice(5).trim();
        if (!raw || raw === '[DONE]') return;
        let parsed: unknown = raw;
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = raw;
        }
        collectConversationIds(parsed, conversationIds);
        const piece = textPiece(eventType, parsed);
        if (piece) pieces.push(piece);
      });
  });
  return { pieces, remaining, conversationIds };
}

function textPiece(eventType: string, parsed: unknown): string {
  if (eventType === 'END_OF_STREAM' || eventType === 'error') return '';
  if (typeof parsed === 'string') {
    return !eventType || eventType === 'LLM_ANSWER' || eventType === 'message' ? parsed : '';
  }
  if (!parsed || typeof parsed !== 'object') return '';
  const record = parsed as Record<string, unknown>;
  const type = String(record.type || eventType || '');
  if (type === 'ASSISTANT_STREAMED_RESPONSE' && record.streamingResponse && typeof record.streamingResponse === 'object') {
    const stream = record.streamingResponse as Record<string, unknown>;
    if (stream.type === 'LLM_ANSWER' && typeof stream.data === 'string') return stream.data;
  }
  if ((type === 'LLM_ANSWER' || eventType === 'LLM_ANSWER') && typeof record.data === 'string') return record.data;
  if (typeof record.content === 'string') return record.content;
  return '';
}

/**
 * Ask Buddy and report the streamed reply as it arrives. The first message of a conversation goes
 * to Klax's business service, which sends it on its own model, not billed to the member; the agent and
 * the text are sent, signed so the service knows they came from this app. Follow-ups carry the
 * conversation id and go to blocks-agents as ordinary turns. A message the user typed always goes to
 * blocks-agents, which starts a new temporary conversation when the page line never got an id.
 *
 * Resolves true when the call failed, for any reason and any status, before a word of the reply
 * arrived, so the caller can show its written answer instead. Abandoning the ask is not a failure.
 */
export async function streamBuddyReply(
  content: string,
  onText: (text: string) => void,
  signal?: AbortSignal,
  typedByUser = false
): Promise<boolean> {
  if (!content.trim() || signal?.aborted) return false;
  const conversationId = conversationIdForCompletion();
  const toAgent = Boolean(conversationId) || typedByUser;
  let failed = false;
  await runDuringBuddyAsk(async () => {
    let text = '';
    try {
      const config = await getConfigurationSettings();
      if (signal?.aborted) return;
      const serviceBase = (config.paths.klaxServiceBaseUrl || '').replace(/\/$/, '');
      const reader = await clients.stream(
        toAgent
          ? `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi.collaborationCompletion}`
          : `${serviceBase}${config.paths.klaxAccessCompletion}`,
        config.blocksKey,
        JSON.stringify(
          toAgent
            ? {
                isStreaming: false,
                ...(conversationId ? { conversationId } : { isTemporary: true, agentId: BUDDY_CONFIG.agentId }),
                message: { content },
              }
            : { agentId: BUDDY_CONFIG.agentId, content }
        ),
        {
          Accept: 'text/event-stream',
          ...(toAgent ? {} : { [KLAX_ACCESS_HEADER]: await klaxAccessHeader(config.klaxAccessKey) }),
        },
        signal
      );
      const decoder = new TextDecoder();
      let buffer = '';
      while (!signal?.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const read = readCompletionChunks(buffer);
        buffer = read.remaining;
        read.conversationIds.forEach(rememberConversationId);
        if (!read.pieces.length) continue;
        text += read.pieces.join('');
        onText(text);
      }
      failed = !text && !signal?.aborted;
    } catch {
      failed = !text && !signal?.aborted;
    }
  });
  return failed;
}
